import { useEffect } from 'react';
import LinearProgress from '@material-ui/core/LinearProgress';
import _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { Button } from '@material-ui/core';
import { useStyles } from './FormStyles';
import DirtyFormConfirm from './DirtyFormConfirm';

const defaultFormId = 'frmMain';

function focusFirstField(formId, focusFieldId) {
    const inputTypeText = 'text';
    const inputTypePassword = 'password';

    if (focusFieldId) {
        const focusElement = document.getElementById(focusFieldId);
        if (focusElement) {
            focusElement.focus();
        }
    }

    const formInputElements = document?.forms[formId]?.getElementsByTagName('input');
    if (!formInputElements) {
        return;
    }

    const firstElement = _.first(
        _.filter(
            formInputElements,
            (element) =>
                (element.type === inputTypeText || element.type === inputTypePassword) &&
                !element.disabled &&
                !element.hidden &&
                !/MuiSelect/.test(element.className)
        )
    );

    if (firstElement) {
        firstElement.focus();
    }
}

const defaultSaveButtonLabel = i18n._(t`Save`);

export default function NgpForm({
    formId = defaultFormId,
    children,
    submitFunction,
    initialized,
    loading,
    showSave = true,
    formPadding = true,
    focusFirstField: focusFirstFieldProp,
    focusFieldId,
    isDirtyFunc,
    excludeFromDirtyCheck,
    showRequiredFieldsTip = true,
    saveButtonLabel = defaultSaveButtonLabel,
    saveButtonDisabled = false,
}) {
    const classes = useStyles();
    const { handleSubmit } = useFormContext();

    useEffect(() => {
        if (focusFirstFieldProp !== false) {
            setTimeout(() => focusFirstField(formId, focusFieldId), 500);
        }
        // can't watch the other deps
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialized]);

    return (
        <div className="scroll-container">
            {loading && <LinearProgress />}

            {showRequiredFieldsTip && (
                <div className={classes.requiredFieldsTip}>
                    <Trans>* Required Fields</Trans>
                </div>
            )}

            {initialized && (
                <form
                    className={classes.root}
                    id={formId}
                    name={formId}
                    onSubmit={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleSubmit(submitFunction)(e);
                    }}
                >
                    <DirtyFormConfirm isDirtyFunc={isDirtyFunc} excludeFromDirtyCheck={excludeFromDirtyCheck} />
                    <div className={formPadding ? classes.ngpForm : classes.ngpFormNoPadding}>
                        {children}

                        <div className={classes.ngpBottomActions}>
                            <Button
                                type="submit"
                                id="childSubmitButton"
                                variant="contained"
                                color="primary"
                                form={formId}
                                className={showSave ? '' : 'hideSave'}
                                disabled={saveButtonDisabled}
                            >
                                {saveButtonLabel}
                            </Button>
                        </div>
                    </div>
                </form>
            )}
        </div>
    );
}

NgpForm.defaultProps = {
    submitFunction: _.noop,
    focusFirstField: true,
};

NgpForm.propTypes = {
    submitFunction: PropTypes.func,
    focusFirstField: PropTypes.bool,
};
